import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Check, ChevronsUpDown, RefreshCw, Sparkles } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Switch,
  cn,
} from "@nexus/ui";
import type { PromptLlmModel, PromptLlmModelTier, PromptTaskType } from "@nexus/contracts";
import { PromptModelProviderIcon } from "@/features/prompts/components/PromptModelProviderIcon";
import {
  PROMPT_MODEL_SERVICE_DEFAULT_VALUE,
  clusterPromptModelsByFamily,
  groupPromptModelsByProvider,
  incapabilityReasonKey,
  inferPromptModelProvider,
  isVisibleByDefault,
  promptModelBlockedReason,
  type PromptModelBlockedReason,
  type PromptModelFamilyCluster,
} from "@/features/prompts/lib/prompt-model-ui";

interface PromptModelSelectProps {
  id: string;
  value: string | null;
  onChange: (modelId: string | null) => void;
  models: readonly PromptLlmModel[];
  suggestedModel: PromptLlmModel | null;
  taskType: PromptTaskType;
  /** Call site key — decides which providers can actually run the choice. */
  usedIn: string;
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function tierLabel(t: TFunction, tier: PromptLlmModelTier): string {
  if (tier === "economy") return t("prompts.editor.model.tierEconomy", "Economy");
  if (tier === "premium") return t("prompts.editor.model.tierPremium", "Premium");
  return t("prompts.editor.model.tierStandard", "Standard");
}

function providerLabel(t: TFunction, provider: PromptLlmModel["provider"]): string {
  return provider === "anthropic"
    ? t("prompts.editor.model.providerAnthropic", "Anthropic")
    : t("prompts.editor.model.providerGemini", "Gemini");
}

/** A family whose SKUs all share a tier states it once in the heading. */
function sharedFamilyTier(
  models: readonly PromptLlmModel[],
): PromptLlmModelTier | null {
  const first = models[0];
  if (!first) return null;
  return models.every((model) => model.tier === first.tier) ? first.tier : null;
}

function familyLabel(t: TFunction, cluster: PromptModelFamilyCluster): string {
  if (cluster === "opus") return t("prompts.editor.model.groupOpus", "Opus");
  if (cluster === "sonnet") return t("prompts.editor.model.groupSonnet", "Sonnet");
  if (cluster === "haiku") return t("prompts.editor.model.groupHaiku", "Haiku");
  if (cluster === "pro") return t("prompts.editor.model.groupPro", "Pro");
  if (cluster === "flash") return t("prompts.editor.model.groupFlash", "Flash");
  if (cluster === "image") return t("prompts.editor.model.groupImage", "Image");
  return t("prompts.editor.model.groupOther", "Other");
}

function ModelRow({
  provider,
  label,
  meta,
  hint,
  badge,
  isSelected,
  disabled,
}: {
  provider: PromptLlmModel["provider"] | "service";
  label: string;
  meta?: string;
  hint?: string;
  badge?: string;
  isSelected: boolean;
  disabled?: boolean;
}) {
  return (
    <>
      <PromptModelProviderIcon provider={provider} className="mt-px self-start" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[13px] tracking-[-0.01em]">{label}</span>
          {badge ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-ai-subtle px-1.5 py-px text-[10px] font-medium text-ai-subtle-foreground">
              <Sparkles className="size-2.5 text-ai-subtle-foreground" aria-hidden />
              {badge}
            </span>
          ) : null}
        </span>
        {hint ? (
          <span className="truncate text-[11px] text-muted-foreground">{hint}</span>
        ) : null}
      </span>
      {meta ? (
        <span className="shrink-0 self-start pt-0.5 text-[11px] tabular-nums text-muted-foreground">
          {meta}
        </span>
      ) : null}
      <Check
        aria-hidden
        className={cn(
          "size-3.5 shrink-0 self-start text-primary transition-opacity duration-150 ease-out",
          isSelected && !disabled ? "opacity-100" : "opacity-0",
        )}
      />
    </>
  );
}

/**
 * Model picker for the prompt editor: type-to-filter over the live catalog,
 * filtered by task capability, with an optional "Show all" escape hatch.
 */
export function PromptModelSelect({
  id,
  value,
  onChange,
  models,
  suggestedModel,
  taskType,
  usedIn,
  isLoading,
  isRefreshing,
  onRefresh,
}: PromptModelSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Locked models are hidden until asked for: the toggle only renders when some
  // exist, so it is the signpost that there is more to see.
  const [hideUnavailable, setHideUnavailable] = useState(true);

  const blockedReason = useCallback(
    (model: PromptLlmModel) => promptModelBlockedReason(model, usedIn, taskType),
    [taskType, usedIn],
  );

  const runnableModels = useMemo(
    () => models.filter((m) => blockedReason(m) === null),
    [blockedReason, models],
  );
  const hasUnavailable = useMemo(() => models.some((m) => m.unavailable), [models]);

  const visibleModels = useMemo(() => {
    const base = showAll
      ? models
      : models.filter((m) => isVisibleByDefault(blockedReason(m)));
    if (!hideUnavailable) return base;
    // The current pick stays listed while hidden, so the list never contradicts
    // the trigger.
    return base.filter((m) => !m.unavailable || m.id === value);
  }, [blockedReason, hideUnavailable, models, showAll, value]);

  const selectedModel = useMemo(() => {
    if (!value) return null;
    return (
      models.find((m) => m.id === value) ??
      (suggestedModel?.id === value ? suggestedModel : null)
    );
  }, [models, suggestedModel, value]);

  const selectedBlockedReason = selectedModel ? blockedReason(selectedModel) : null;
  const isMissingFromCatalog = Boolean(value && !selectedModel);
  const providerGroups = useMemo(
    () => groupPromptModelsByProvider(visibleModels, suggestedModel?.id),
    [visibleModels, suggestedModel?.id],
  );

  // cmdk matches rendered children by default, which breaks with custom rows.
  const searchTextById = useMemo(() => {
    const map = new Map<string, string>();
    map.set(
      PROMPT_MODEL_SERVICE_DEFAULT_VALUE,
      t("prompts.editor.model.serviceDefault", "Service default"),
    );
    for (const model of visibleModels) {
      map.set(
        model.id,
        `${model.label} ${model.id} ${model.provider} ${tierLabel(t, model.tier)}`,
      );
    }
    if (suggestedModel) {
      map.set(
        suggestedModel.id,
        `${suggestedModel.label} ${suggestedModel.id} ${suggestedModel.provider} ${tierLabel(t, suggestedModel.tier)}`,
      );
    }
    if (value && isMissingFromCatalog) map.set(value, value);
    return map;
  }, [isMissingFromCatalog, visibleModels, suggestedModel, t, value]);

  const filter = useCallback(
    (itemValue: string, search: string) => {
      const haystack = searchTextById.get(itemValue) ?? itemValue;
      return haystack.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;
    },
    [searchTextById],
  );

  const handleSelect = (nextValue: string) => {
    const nextId =
      nextValue === PROMPT_MODEL_SERVICE_DEFAULT_VALUE ? null : nextValue;
    if (nextId) {
      const model = models.find((m) => m.id === nextId);
      if (model && blockedReason(model) !== null) return;
    }
    onChange(nextId);
    setOpen(false);
  };

  const triggerProvider = selectedModel?.provider ?? inferPromptModelProvider(value);

  const triggerLabel =
    selectedModel?.label ??
    value ??
    t("prompts.editor.model.serviceDefault", "Service default");

  const incapableHint = t(incapabilityReasonKey(taskType), {
    defaultValue:
      taskType === "image"
        ? "No image output"
        : taskType === "vision"
          ? "No video / file input"
          : "No text generation",
  });

  const wrongProviderHint = t(
    "prompts.editor.model.wrongProvider",
    "Not served at this call site",
  );

  const unavailableHint = t(
    "prompts.editor.model.unavailable",
    "Not available to this project",
  );

  const blockedHint = (reason: PromptModelBlockedReason): string => {
    if (reason === "unavailable") return unavailableHint;
    if (reason === "provider") return wrongProviderHint;
    return incapableHint;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-sm border border-input-border bg-input-background px-3 text-left text-[13px] text-foreground outline-none transition-colors duration-150 ease-out",
            "hover:border-input-focus-border focus-visible:border-input-focus-border focus-visible:ring-2 focus-visible:ring-ring/30",
            open && "border-input-focus-border",
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <PromptModelProviderIcon provider={triggerProvider} />
            <span className="truncate">{triggerLabel}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {selectedModel ? (
              <span className="text-[11px] text-muted-foreground">
                {tierLabel(t, selectedModel.tier)}
              </span>
            ) : null}
            <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        className="flex max-h-[min(var(--radix-popover-content-available-height),22rem)] w-(--radix-popover-trigger-width) flex-col overflow-hidden p-0"
      >
        <Command
          filter={filter}
          defaultValue={value ?? PROMPT_MODEL_SERVICE_DEFAULT_VALUE}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <CommandInput
            placeholder={t("prompts.editor.model.search", "Search models…")}
          />
          <CommandList className="max-h-none min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {isLoading && models.length === 0 ? (
              <div
                role="status"
                aria-label={t("prompts.editor.model.loading", "Loading models…")}
                className="space-y-1.5 p-2"
              >
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-7 w-full" />
                ))}
              </div>
            ) : (
              <>
                <CommandEmpty>
                  {t(
                    "prompts.editor.model.empty",
                    "No model matches — try “opus” or “flash”.",
                  )}
                </CommandEmpty>

                <CommandGroup>
                  <CommandItem
                    value={PROMPT_MODEL_SERVICE_DEFAULT_VALUE}
                    onSelect={handleSelect}
                    className="items-start"
                  >
                    <ModelRow
                      provider="service"
                      label={t(
                        "prompts.editor.model.serviceDefault",
                        "Service default",
                      )}
                      hint={t(
                        "prompts.editor.model.serviceDefaultHint",
                        "Each call site keeps its configured model",
                      )}
                      isSelected={value === null}
                    />
                  </CommandItem>
                </CommandGroup>

                {suggestedModel && blockedReason(suggestedModel) === null ? (
                  <>
                    <CommandSeparator />
                    <CommandGroup
                      heading={t("prompts.editor.model.suggestedGroup", "Suggested")}
                    >
                      <CommandItem
                        value={suggestedModel.id}
                        onSelect={handleSelect}
                        className="items-start"
                      >
                        <ModelRow
                          provider={suggestedModel.provider}
                          label={suggestedModel.label}
                          badge={t(
                            "prompts.editor.model.suggestedBadge",
                            "Suggested",
                          )}
                          hint={t(
                            "prompts.editor.model.suggestedHint",
                            "Matches this call site's provider and workload",
                          )}
                          meta={tierLabel(t, suggestedModel.tier)}
                          isSelected={value === suggestedModel.id}
                        />
                      </CommandItem>
                    </CommandGroup>
                  </>
                ) : null}

                {providerGroups.map((group) =>
                  clusterPromptModelsByFamily(group.models).map((family) => {
                    const sharedTier = sharedFamilyTier(family.models);
                    const name =
                      family.cluster === "other"
                        ? providerLabel(t, group.provider)
                        : familyLabel(t, family.cluster);
                    return (
                      <Fragment key={`${group.provider}-${family.cluster}`}>
                        <CommandSeparator />
                        <CommandGroup
                          heading={
                            sharedTier
                              ? t(
                                  "prompts.editor.model.groupHeading",
                                  "{{family}} · {{tier}}",
                                  { family: name, tier: tierLabel(t, sharedTier) },
                                )
                              : name
                          }
                        >
                          {family.models.map((model) => {
                            const reason = blockedReason(model);
                            const disabled = reason !== null;
                            return (
                              <CommandItem
                                key={model.id}
                                value={model.id}
                                disabled={disabled}
                                onSelect={handleSelect}
                                className={cn(
                                  "items-start",
                                  disabled && "opacity-50",
                                )}
                              >
                                <ModelRow
                                  provider={model.provider}
                                  label={model.label}
                                  meta={
                                    sharedTier ? undefined : tierLabel(t, model.tier)
                                  }
                                  hint={
                                    disabled && reason ? blockedHint(reason) : undefined
                                  }
                                  isSelected={value === model.id}
                                  disabled={disabled}
                                />
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </Fragment>
                    );
                  }),
                )}

                {isMissingFromCatalog && value ? (
                  <>
                    <CommandSeparator />
                    <CommandGroup
                      heading={t(
                        "prompts.editor.model.unavailableGroup",
                        "Unavailable",
                      )}
                    >
                      <CommandItem
                        value={value}
                        onSelect={handleSelect}
                        className="items-start"
                      >
                        <ModelRow
                          provider={triggerProvider}
                          label={value}
                          meta={t("prompts.editor.model.retired", "not in catalog")}
                          isSelected
                        />
                      </CommandItem>
                    </CommandGroup>
                  </>
                ) : null}
              </>
            )}
          </CommandList>
        </Command>

        <div className="flex shrink-0 flex-col gap-1.5 border-t border-border px-2.5 py-1.5">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground">
                <Switch
                  checked={showAll}
                  onCheckedChange={setShowAll}
                  aria-label={t(
                    "prompts.editor.model.showAllAria",
                    "Show all models including incompatible ones",
                  )}
                />
                {t("prompts.editor.model.showAll", "Show all models")}
              </label>
              {hasUnavailable ? (
                <label className="flex cursor-pointer items-center gap-2 text-[11px] text-muted-foreground">
                  <Switch
                    checked={hideUnavailable}
                    onCheckedChange={setHideUnavailable}
                    aria-label={t(
                      "prompts.editor.model.hideUnavailableAria",
                      "Hide models this project cannot use",
                    )}
                  />
                  {t("prompts.editor.model.hideUnavailable", "Hide unavailable")}
                </label>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-1 text-[11px] text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={cn("size-3", isRefreshing && "animate-spin")}
                aria-hidden
              />
              {t("prompts.editor.model.refresh", "Refresh models")}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">
              {t("prompts.editor.model.countAvailable", {
                count: runnableModels.length,
              })}
            </span>
            {selectedBlockedReason ? (
              <span className="text-[11px] text-[var(--warning)]">
                {blockedHint(selectedBlockedReason)}
              </span>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
